import produce from "immer";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactResizeDetector from "react-resize-detector";

import { ViewerInitState, viewerType } from "../ViewerLayout/ViewerLayoutTypes";
import { clickType, offsetType, viewerState as _viewerState } from "../ViewerLayout/ViewerTypes";
import { Graph } from "./Graph";
import styles from "./SpectrumComponent.module.css";
import { ViewerNavigation } from "./ViewerNavigation/ViewerNavigation";
import { data1DType as _data1DType, data2DMatrixType as _data2DMatrixType } from "./ViewerTrackType";
import { SessionContext } from "../common/contexts/SessionContext";
import { globalViewerState } from "../ViewerLayout/useViewerState";

// import { TreeViewer } from "./TreeViewer";
// type trackType = _trackType;
// export type trackParameterType = _trackParameterType;
// export const initTrackParameterType = _initTrackParameterType;
// export const initTrackType = _initTrackType;
export type data1DType = _data1DType;
export type data2DMatrixType = _data2DMatrixType;
export type SpectrumComponentState = _viewerState;
// export type annotationType = _annotationType;
// export type dataTrackType = _dataTrackType;
// export type drawType = _drawType;

// export type trackType = {
//   id?: string;
//   type: "1D_real" | "1D_complex" | "2D_real_matrix";
//   data?: any;
//   parameter: graphTrackParameterType;
// };

// const getSelectedTracks = (tracks: Record<string, Track>) => {
//   const selected = Object.values(tracks)
//     .filter((track) => track.settings.active && track.settings.selected)
//     .map((track) => track.id);

//   if (selected.length === 0) return Object.values(tracks).map((track) => track.id);
//   return selected;
// };

const sanitizeFilename = (input: string): string => {
  // Remove invalid characters: \ / : * ? " < > | and control chars
  return input
    // We will ignore that these characters are not allowed in regex as there is not other way to remove them all in one go
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\\/:*?"<>|]+/g, "") 
    .replace(/\s+/g, "_") // Optionally replace whitespace with underscores
    .replace(/_+/g, "_") // Collapse multiple underscores
    .replace(/^_+|_+$/g, "") // Trim leading/trailing underscores
    .slice(0, 255); // Limit length for most filesystems
};

export const SpectrumComponent2D = (props: globalViewerState) => SpectrumComponent(props);

export const SpectrumComponent = ({
  state,
  viewerVisualState,
  setViewerVisualState,
  viewerInteractionSettings,
  setTracksToZoom,
  // setZoomMode,
  setViewerMode,
  // selectedTracks,
  changeTrackState,
  changeTrackSettings,
}: globalViewerState) => {
  const { tracks, viewerMode, viewPort, activeViewer, datatracks, annotations, tracksToZoom, parserLogs } = state;

  const { showNavigation, interactiveMode } = viewerInteractionSettings;
  const [offsetState, setOffsetState] = useState<offsetType>({});
  const [autoZoom, setAutoZoom] = useState(viewerMode.autoZoom);
  const [lastClick, setLastClick] = useState<clickType>({ count: 0, x: 0, y: 0 });
  const [currentActiveViewer, setCurrentActiveViewer] = useState<viewerType | undefined>();
  const [visualState, setVisualState] = useState<SpectrumComponentState>();
  const [viewerType, setViewerType] = useState<viewerType>();
  const graphRef = useRef();
  // const [visualState, setVisualState] = useState<ViewerInitState>({});
  // const selectedTracks = useTranslatedFieldUpdate(tracks, getSelectedTracks, []);
  const { api } = useContext(SessionContext);

  // Freeze the viewer type when it is set for the first time and keep
  useEffect(() => {
    if (!viewerType) setViewerType(activeViewer);
    // log cleanup
    // TODO: check dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViewer]);

  // Set the visual state when the viewer type changes (viewer is reactivated)
  useEffect(() => {
    if (activeViewer)
      if (activeViewer !== currentActiveViewer) {
        setCurrentActiveViewer(activeViewer);
        setVisualState(viewerVisualState[activeViewer] as SpectrumComponentState);
      }
  }, [activeViewer, currentActiveViewer, viewerVisualState]);

  // Update the stored visual state based on the viewer type
  const updateVisualState = useCallback(
    (newState: ViewerInitState) => {
      if (!viewerType || !setViewerVisualState) return;
      setViewerVisualState(viewerType, newState);
    },
    [viewerType, setViewerVisualState]
  );

  const resetZoom = useCallback(
    () =>
      setTracksToZoom
        ? setTracksToZoom(Object.keys(tracks).filter((id) => tracks[id].settings.visible && tracks[id].settings.active))
        : null,
    [setTracksToZoom, tracks]
  );

  const getTranslateScale = useCallback(() => {
    if (graphRef?.current) return (graphRef.current as any).getTranslateScale();
    return {};
  }, []);

  const setNewOffset = useCallback(
    (value: offsetType) => {
      setOffsetState(
        produce(offsetState, (next) => {
          if (value?.translateX !== undefined) next.translateX = value.translateX;
          if (value?.translateY !== undefined) next.translateY = value.translateY;
          // Object.entries(value).forEach(([k, v]) => (next[k as keyof offsetType] = v));
        })
      );
      // }
    },
    [offsetState]
  );

  useEffect(() => {
    if (setViewerMode)
      setViewerMode(
        produce(viewerMode, (next) => {
          next.autoZoom = autoZoom;
        })
      );

    // set viewerMode is set here and the effect should not be dependent on it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoZoom, setViewerMode]);

  const onExport = useCallback(
    (type: string) => {
      console.log(`Export as '${type}' '${Object.values(state.datasets)[0]?.name}'`);
      if (graphRef?.current) {
        (graphRef.current as any).exportAs(type, `${sanitizeFilename(Object.values(state.datasets)[0]?.name)}`);

        // the following is for the case where the export is done by ViewerLayout
        // if (type === "csv")
        //   onDataExport(
        //     type,
        //     Object.values(tracks)
        //       .filter((track) => track.settings.active && track.settings.visible)
        //       .map((track) => track.id)
        //   );
        // else (graphRef.current as any).exportAs(type);
      }
    },
    [graphRef, state.datasets]
  );

  const onZoomUpdate = useCallback(() => {
    if (viewerMode.zoomUpdate)
      if (setViewerMode)
        setViewerMode(
          produce(viewerMode, (next) => {
            next.zoomUpdate = false;
          })
        );
  }, [setViewerMode, viewerMode]);

  return (
    <div id="viewer-container" className={styles.viewer}>
      {showNavigation === undefined || showNavigation === true ? (
        <div className={styles.viewerNavigation}>
          <ViewerNavigation
            // className={styles.viewerNavigation}
            resetZoom={resetZoom}
            parserLogs={parserLogs}
            // setZoomMode={(zoomMode: zoomModes) =>
            //   setViewerMode(
            //     produce(viewerMode, (next) => {
            //       next.zoomMode = zoomMode;
            //     })
            //   )
            // }
            setViewerMode={setViewerMode ?? (() => null)}
            // setViewerMode={() => {}}
            viewerMode={viewerMode}
            tracks={tracks}
            datatracks={datatracks}
            // selectedTracks={selectedTracks}
            // selectedTracks={[]}
            // setSliderCurrent={setActiveTrack}
            changeTrackSettings={changeTrackSettings ?? (() => null)}
            autoZoom={autoZoom}
            setAutoZoom={setAutoZoom}
            onExport={onExport}
            setOffset={setNewOffset}
            // offset={offset}
            getTranslateScale={getTranslateScale}
            viewPort={viewPort}
            graphClick={lastClick}
          />
        </div>
      ) : null}

      <ReactResizeDetector handleWidth handleHeight>
        <Graph
          api={api}
          tracks={tracks}
          activeTrack={""}
          tracksToZoom={tracksToZoom}
          annotations={annotations}
          datatracks={datatracks}
          viewerMode={viewerMode}
          // activeTrack={activeTrack}
          changeTrackSettings={changeTrackSettings ?? (() => null)}
          changeTrackState={changeTrackState ?? (() => null)}
          setViewerState={updateVisualState}
          autoZoom={autoZoom}
          ref={graphRef}
          offset={offsetState}
          interactiveMode={interactiveMode}
          zoomUpdate={viewerMode.zoomUpdate}
          onZoomUpdate={onZoomUpdate}
          clickListener={(pos: { x: number | undefined; y: number | undefined }) =>
            setLastClick(
              produce(lastClick, (next) => {
                next.count++;
                next.x = pos.x ?? Number.NaN;
                next.y = pos.y ?? Number.NaN;
              })
            )
          }
          initViewerState={visualState}
          // {...otherProps}
        />
      </ReactResizeDetector>
    </div>
  );
};
